import React, { useState, useEffect, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, Ticket, Target, X, Loader2, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ChallengeDrawStatus from '@/components/ChallengeDrawStatus';
import TicketCounter from '@/components/TicketCounter';
import { useViewedChallenges } from '@/components/useViewedChallenges';
import { usePullToRefresh } from '@/components/usePullToRefresh';

const createPageUrl = (pageName) => {
  const [base, query] = pageName.split('?');
  return query ? `/${base}?${query}` : `/${base}`;
};

export default function Challenges({ onClose, isActive }) {
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { markMultipleAsViewed } = useViewedChallenges(user?.id);

  // Pull-to-refresh: invalidate challenges-related queries //
  const { isPulling, pullDistance } = usePullToRefresh(
    useCallback(async () => {
      await queryClient.invalidateQueries({ queryKey: ['relevantChallenges'] });
      await queryClient.invalidateQueries({ queryKey: ['userProgress'] });
      await queryClient.invalidateQueries({ queryKey: ['userTickets'] });
      await queryClient.invalidateQueries({ queryKey: ['userPlaygrounds'] });
    }, [queryClient]),
    !onClose // Only enabled in standalone mode
  );

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user: authUser } }) => setUser(authUser)).catch(() => setUser(null));
  }, []);

  // Refetch data when tab becomes active (TabsLayout keeps component mounted)
  useEffect(() => {
    if (isActive && user?.id) {
      queryClient.invalidateQueries({ queryKey: ['relevantChallenges'] });
      queryClient.invalidateQueries({ queryKey: ['userProgress'] });
      queryClient.invalidateQueries({ queryKey: ['userTickets'] });
    }
  }, [isActive, user?.id, queryClient]);

  // Auto-scroll to bottom on mobile
  useEffect(() => {
      if (window.innerWidth < 768) {
          setTimeout(() => {
              window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          }, 100);
      }
  }, []);

  const { data: userPlaygrounds = [] } = useQuery({
    queryKey: ['userPlaygrounds', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      return await base44.entities.Playground.filter({
        created_by_user_id: user.id
      });
    },
    enabled: !!user?.id
  });

  const { data: activeChallenges = [] } = useQuery({
    queryKey: ['relevantChallenges', user?.id, userPlaygrounds.length],
    queryFn: async () => {
      const now = new Date().toISOString();
      const allChallenges = await base44.entities.Challenge.filter({
        is_active: true
      });

      // Filter to only those within time window
      const timeChallenges = allChallenges.filter(c =>
        new Date(c.start_at) <= new Date(now) &&
        new Date(c.end_at) >= new Date(now)
      );

      // Show all time-relevant challenges
      return timeChallenges.sort((a, b) => new Date(a.end_at) - new Date(b.end_at));
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  const { data: userTickets = {} } = useQuery({
    queryKey: ['userTickets', user?.id, activeChallenges.length],
    queryFn: async () => {
      if (!user?.id) return {};
      const entries = await base44.entities.RaffleEntry.filter({
        user_id: user.id
      });
      // Group tickets by challenge
      const ticketsByChallenge = {};
      entries.forEach(entry => {
        if (!ticketsByChallenge[entry.challenge_id]) {
          ticketsByChallenge[entry.challenge_id] = 0;
        }
        ticketsByChallenge[entry.challenge_id]++;
      });
      return ticketsByChallenge;
    },
    enabled: !!user?.id && activeChallenges.length > 0,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  const { data: userProgress = {} } = useQuery({
    queryKey: ['userProgress', user?.id],
    queryFn: async () => {
      if (!user?.id) return {};
      const progress = await base44.entities.UserChallengeProgress.filter({
        user_id: user.id
      });
      const map = {};
      progress.forEach(p => {
        map[p.challenge_id] = p;
      });
      return map;
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  // Mark all challenges as viewed when page opens
  const isPageActive = isActive !== false;

  useEffect(() => {
      if (!user?.id) return;
      if (!isPageActive) return;
      if (activeChallenges.length > 0) {
          markMultipleAsViewed(activeChallenges.map(c => c.id));
      }
  }, [activeChallenges, markMultipleAsViewed, user?.id, isPageActive]);

  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#0A0B10]">
        <div className="text-center">
          <p className="text-white mb-4">Connectez-vous pour voir les challenges</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white px-6 py-2 rounded-lg"
          >
            Connexion
          </button>
        </div>
      </div>
    );
  }

  const content = (
      <div className="p-4 md:p-6">
        {/* Pull-to-refresh indicator */}
        {isPulling && (
          <div
            className="flex items-center justify-center transition-all duration-150 -mt-4 mb-2"
            style={{ height: Math.min(pullDistance, 80), overflow: 'hidden' }}
          >
            <Loader2
              className={`w-5 h-5 text-[#6E2BFF] ${pullDistance > 80 ? 'animate-spin' : ''}`}
              style={{ opacity: Math.min(pullDistance / 80, 1) }}
            />
          </div>
        )}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-lg font-black uppercase tracking-wide text-white">Défis</h2>
            <p className="text-[#B3B6C2] text-xs md:text-sm mt-2">1 défi complété = plus de chances au tirage</p>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:text-[#6E2BFF]"
            >
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>

      <div className="max-w-4xl mx-auto">
        {activeChallenges.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#B3B6C2]">Aucun challenge actif pour le moment</p>
          </div>
        ) : (
          <div className="grid gap-3 md:gap-4">
            {[...activeChallenges].sort((a, b) => {
              const aLocked = (a.min_level || 1) > (user?.level || 1);
              const bLocked = (b.min_level || 1) > (user?.level || 1);
              if (aLocked !== bLocked) return aLocked ? 1 : -1;
              return new Date(a.end_at) - new Date(b.end_at);
            }).map(challenge => {
                const progress = userProgress[challenge.id];
                const isCompleted = progress?.is_completed || false;
                const currentProgress = progress?.progress_value || 0;
                const progressPercent = Math.min(100, Math.round((currentProgress / challenge.condition_value) * 100));
                const isLocked = (challenge.min_level || 1) > (user?.level || 1);

                return (
                  <Card
                    key={challenge.id}
                    className={`relative overflow-hidden p-4 md:p-6 transition-all ${
                      isLocked
                        ? 'bg-[#13151D] border-2 border-[#2A2D3A]'
                        : 'bg-gradient-to-br from-[#13151D] to-[#1B1E28] border-2 border-[#6E2BFF] hover:border-[#8B5CFF] hover:shadow-lg hover:shadow-[#6E2BFF]/20'
                    }`}
                  >
                    <div className="space-y-3 md:space-y-5">
                      {/* Locked banner */}
                      {isLocked && (
                        <div className="flex items-center gap-2 bg-[#2A2D3A]/60 rounded-lg px-3 py-2 border border-[#3A3D4A]/50">
                          <Lock className="w-4 h-4 text-[#9CA0AB] flex-shrink-0" />
                          <p className="text-xs font-bold text-[#9CA0AB]">
                            Niveau {challenge.min_level} requis — Ton niveau : {user?.level || 1}
                          </p>
                        </div>
                      )}

                      {/* Header with Status */}
                      <div className="flex flex-col md:flex-row items-start justify-between gap-3 md:gap-4">
                        <div className="flex-1 min-w-0">
                          <h2 className={`text-base md:text-lg font-black uppercase tracking-tight mb-2 ${isLocked ? 'text-[#9CA0AB]' : 'text-white'}`}>
                            {challenge.title}
                          </h2>
                          <p className="text-[#B3B6C2] text-xs md:text-sm leading-relaxed">{challenge.description}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {(challenge.min_level || 1) > 1 && (
                            <Badge className={`text-xs font-bold px-2 py-1 ${
                              isLocked
                                ? 'bg-[#2A2D3A]/40 border border-[#3A3D4A]/50 text-[#9CA0AB]'
                                : 'bg-[#6E2BFF]/20 border border-[#6E2BFF]/40 text-[#B3B6C2]'
                            }`}>
                              Niv. {challenge.min_level}
                            </Badge>
                          )}
                          {isCompleted && (
                            <Badge className="bg-green-500/20 border border-green-500 text-green-400 text-xs font-bold uppercase px-2 md:px-3 py-1">
                              ✓ COMPLÉTÉ
                            </Badge>
                          )}
                        </div>
                      </div>

                     {/* Objective Section */}
                     <div className={`bg-[#0A0B10] rounded-lg p-3 md:p-4 border ${isLocked ? 'border-[#2A2D3A]/50' : 'border-[#6E2BFF]/30'}`}>
                       <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                         <Target className={`w-4 h-4 md:w-5 md:h-5 ${isLocked ? 'text-[#9CA0AB]' : 'text-[#6E2BFF]'}`} />
                         <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-bold">Objectif</p>
                       </div>
                       <p className="text-white font-bold text-base md:text-lg">
                         {challenge.condition_value} {
                           challenge.condition_type === 'add_court' ? 'terrain(s) à ajouter' :
                           challenge.condition_type === 'activate_court' ? 'terrain(s) à activer' :
                           challenge.condition_type === 'win_duels' ? 'duel(s) à remporter' :
                           challenge.condition_type === 'capture_courts' ? 'terrain(s) à capturer' :
                           challenge.condition_type === 'defend_courts' ? 'terrain(s) à défendre' :
                           challenge.condition_type === 'community_growth' ? 'affiliation(s)' :
                           'condition'
                         }
                       </p>
                     </div>

                     {/* Progress Bar */}
                     <div className="space-y-2">
                       <div className="flex items-center justify-between gap-2">
                         <span className="text-[#B3B6C2] text-xs uppercase font-bold tracking-wider">Progression</span>
                         <span className={`text-base md:text-lg font-black ${
                           isLocked ? 'text-[#9CA0AB]' : isCompleted ? 'text-green-500' : 'text-[#6E2BFF]'
                         }`}>
                           {isLocked ? '—' : currentProgress}
                         </span>
                       </div>
                       <div className={`w-full bg-[#0A0B10] rounded-full h-2 md:h-3 overflow-hidden border ${isLocked ? 'border-[#2A2D3A]/30' : 'border-[#6E2BFF]/20'}`}>
                         <div
                           className={`h-full transition-all duration-500 rounded-full ${
                             isLocked ? 'bg-[#2A2D3A]' : isCompleted ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-[#6E2BFF] to-[#8B5CFF]'
                           }`}
                           style={{ width: isLocked ? '0%' : `${progressPercent}%` }}
                         />
                       </div>
                     </div>

                     {/* Reward Section */}
                     <div className={`rounded-lg p-3 md:p-4 border ${
                       isLocked
                         ? 'bg-[#1B1E28]/50 border-[#2A2D3A]/50'
                         : 'bg-gradient-to-r from-[#6E2BFF]/10 to-[#8B5CFF]/10 border-[#6E2BFF]/40'
                     }`}>
                       <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                         <Trophy className={`w-4 h-4 md:w-5 md:h-5 ${isLocked ? 'text-[#9CA0AB]' : 'text-[#6E2BFF]'}`} />
                         <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-bold">À gagner</p>
                       </div>
                       <div className="space-y-2 md:space-y-3">
                         <div className="flex items-baseline gap-2">
                           <Ticket className="w-4 h-4 md:w-5 md:h-5 text-[#6E2BFF] flex-shrink-0" />
                           <div className="min-w-0">
                             <span className="text-lg md:text-2xl font-black text-white">
                               {challenge.reward_value}
                             </span>
                             <span className="text-[#B3B6C2] font-bold text-xs md:text-sm ml-2">
                               chance{challenge.reward_value !== 1 ? 's' : ''} au tirage
                             </span>
                           </div>
                         </div>
                         <div className="flex items-baseline gap-2 pt-2 border-t border-[#6E2BFF]/20">
                           <Trophy className="w-4 h-4 md:w-5 md:h-5 text-yellow-500 flex-shrink-0" />
                           <span className="text-xs md:text-sm text-white font-bold">
                             {challenge.reward_description || 'Billet pour un match Paris Basketball'}
                           </span>
                         </div>
                       </div>
                       {isCompleted && userTickets[challenge.id] > 0 && (
                         <p className="text-xs text-green-400 mt-3 font-semibold">✓ Tirage(s) obtenu(s) - Tu peux gagner !</p>
                       )}
                     </div>

                     {/* Status Indicator */}
                     {isLocked ? (
                       <div className="flex items-center gap-2 text-[#9CA0AB] text-sm font-bold">
                         <Lock className="w-3.5 h-3.5" />
                         Monte au niveau {challenge.min_level} pour participer
                       </div>
                     ) : !isCompleted && (
                       <div className="flex items-center gap-2 text-[#6E2BFF] text-sm font-bold">
                         <div className="w-2 h-2 rounded-full bg-[#6E2BFF] animate-pulse"></div>
                         En cours
                       </div>
                     )}

                     {/* Draw Status */}
                     {challenge.status === 'drawn' && (
                       <ChallengeDrawStatus challenge={challenge} user={user} />
                     )}
                   </div>
                 </Card>
               );
             })}
          </div>
          )}
          </div>
          </div>
          );

          return content;
          }
          